/**
 * Team seats — the price, and whether a seat can actually be bought right now.
 *
 * SOURCE OF TRUTH IS THE SERVER (CLAUDE.md invariant 4, the same rule that
 * killed the hardcoded concierge-fee mirror): `functions/src/billing/plan.ts`
 * composes the plan from env, `/api/health` serves it as `seatPlan`, and this
 * module is a LIVE BINDING seeded with the launch defaults and re-pointed by
 * `hydrateSeatPlan()`, which the existing boot fetch in `askaServices.ts` calls.
 * Nothing here re-fetches: one anonymous `/api/health` at boot already serves
 * the fee, the Stripe publishable key and now this.
 *
 * The seed exists so the first paint is not blank, NOT as a second source of
 * truth: nothing is ever charged from these numbers. The server prices the
 * Stripe session, and the server enforces the seat limit.
 *
 * ── `live` IS THE ONLY THING A PURCHASE CTA MAY BRANCH ON ─────────────────
 * `live: false` (the default, and what production serves until
 * SEATS_BILLING_LIVE is armed) means: render the PRICE all you like — "3 seats
 * free, then $10.99/mo" is true and useful — but do not render a way to buy,
 * because `POST /billing/seats/checkout-session` will answer
 * `{configured:false}` and the person who clicked will conclude the product is
 * broken.
 *
 * ── AND ON THE NATIVE SHELL, NOT EVEN THAT ────────────────────────────────
 * Seats are WEB-ONLY for v1 (docs/ORG-ACCOUNTS.md). The Capacitor shells load
 * the live site remotely, so a routine hosting deploy would otherwise drop a
 * purchase CTA into the reviewed App Store build with no store release — an
 * IAP-policy violation shipped by a deploy nobody thought of as a release. Use
 * `canPurchaseSeats()`, which is `live && !isNativeShell()`, and never `live`
 * alone.
 */
import { isNativeShell } from '../mobile/lib/appMode'

export interface SeatPlan {
  /** Seats that cost nothing, owner included. */
  includedFree: number
  /** The flat fee covers up to this many TOTAL seats. */
  flatUpTo: number
  /** Cents. */
  flatCents: number
  /** Cents, per seat beyond `flatUpTo`. */
  extraSeatCents: number
  currency: string
  interval: 'month'
  /** Can a seat be bought right now? False while the rail is inert. */
  live: boolean
}

/** The locked launch pricing (docs/ORG-ACCOUNTS.md decision 2). Display seed
 *  only — the server is authoritative and re-points this at boot. */
export let SEAT_PLAN: SeatPlan = {
  includedFree: 3,
  flatUpTo: 10,
  flatCents: 1099,
  extraSeatCents: 199,
  currency: 'usd',
  interval: 'month',
  live: false,
}

/**
 * Re-point the plan from `/api/health`, then broadcast `aska:seat-plan-changed`
 * so a mounted paywall recomputes. ESM live bindings mean importers see the new
 * value on their next render; the event is for components that cached a derived
 * string.
 *
 * VALIDATED, not trusted: a malformed payload leaves the seed in place. A
 * missing `live` is FALSE — the safe direction is "no purchase CTA", never
 * "offer a purchase the server will refuse".
 */
export function hydrateSeatPlan(raw: unknown): void {
  if (!raw || typeof raw !== 'object') return
  const p = raw as Partial<Record<keyof SeatPlan, unknown>>
  const int = (v: unknown, fallback: number): number =>
    (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.trunc(v) : fallback)

  const next: SeatPlan = {
    includedFree: int(p.includedFree, SEAT_PLAN.includedFree),
    flatUpTo: int(p.flatUpTo, SEAT_PLAN.flatUpTo),
    flatCents: int(p.flatCents, SEAT_PLAN.flatCents),
    extraSeatCents: int(p.extraSeatCents, SEAT_PLAN.extraSeatCents),
    currency: typeof p.currency === 'string' && p.currency ? p.currency : SEAT_PLAN.currency,
    interval: 'month',
    live: p.live === true,
  }
  if (JSON.stringify(next) === JSON.stringify(SEAT_PLAN)) return
  SEAT_PLAN = next
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('aska:seat-plan-changed'))
  }
}

/**
 * THE gate for anything that lets a person spend money on seats.
 *
 * Two conditions, and the second is not negotiable: the rail is armed
 * server-side, AND we are not inside the native shell. See the header.
 *
 * ⚠️ STEP 11: THIS HAD ZERO NON-TEST CALLERS WHEN IT WAS WRITTEN. A gate
 * nothing calls is a comment, not a guard, so `seatPlan.guard.test.ts` enforces
 * it against the SOURCE TREE: no web file may branch on `SEAT_PLAN.live`, and
 * any file that touches `/billing/seats/checkout-session` or
 * `/billing/seats/portal` must import this function. The server half cannot
 * help — `POST /billing/seats/checkout-session` sees the same origin, the same
 * token and the same everything from both shells, so this predicate is the ONLY
 * thing standing between a hosting deploy and a purchase CTA inside the
 * reviewed App Store build. If that suite fails, call this; never widen it.
 */
export function canPurchaseSeats(): boolean {
  return SEAT_PLAN.live && !isNativeShell()
}

/** 1099 → "$10.99". Mirrors `formatSeatMoney` on the server so the quote a
 *  buyer reads and the amount Stripe charges are formatted from the same rule. */
export function formatSeatPrice(cents: number, currency = SEAT_PLAN.currency): string {
  const amount = (Number.isFinite(cents) ? cents : 0) / 100
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() })
      .format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency.toUpperCase()}`
  }
}

/**
 * What an org of `totalSeats` people pays per month, in cents. The SAME
 * arithmetic as `functions/src/billing/plan.ts::quoteSeats` — duplicated
 * deliberately and narrowly, because a paywall that has to round-trip to price
 * a slider is a paywall that stutters. It is display-only: every number that
 * ends up on an invoice comes back from `POST /billing/seats/checkout-session`.
 */
export function quoteSeatsCents(totalSeats: number): number {
  const n = Number.isFinite(totalSeats) ? Math.max(0, Math.trunc(totalSeats)) : 0
  if (n <= SEAT_PLAN.includedFree) return 0
  const extra = Math.max(0, n - SEAT_PLAN.flatUpTo)
  return SEAT_PLAN.flatCents + extra * SEAT_PLAN.extraSeatCents
}

/** "3 seats free, then $10.99/mo for up to 10" — one sentence, one place, so
 *  the two shells cannot describe the same plan differently. */
export function seatPlanSummary(): string {
  return `${SEAT_PLAN.includedFree} seats free · ${formatSeatPrice(SEAT_PLAN.flatCents)}/mo for up to ${SEAT_PLAN.flatUpTo} · ${formatSeatPrice(SEAT_PLAN.extraSeatCents)}/mo per extra seat`
}

/* ───────────── the tiered seat LADDER (flat-pricing presentation) ──────────
 *
 * docs/PRICING-MODEL.md §2: Aska+ $18.99 (≤3) · Team $39.99 (≤10) · Max $79.99
 * (≤50) — monotonic, ~2× steps, per-seat cost FALLS as the org grows. Served
 * on /api/health as `seatLadder` (LADDER_* env) and hydrated here beside the
 * legacy plan. DISPLAY ONLY, and rendered only while `FLAT_PRICING.live`:
 * with the flag off the paywall keeps the legacy flat-plan presentation
 * byte-for-byte. Purchasing is untouched either way — `canPurchaseSeats()`
 * stays the ONLY predicate a buy control may branch on.
 */

export interface SeatLadderTier {
  key: 'aska_plus' | 'team' | 'max'
  name: string
  priceCents: number
  /** Aska+ seats the tier covers, inclusive. */
  seatCap: number
}

/** Display seed (the locked ladder) — the server re-points this at boot. */
export let SEAT_LADDER: SeatLadderTier[] = [
  { key: 'aska_plus', name: 'Aska+', priceCents: 1899, seatCap: 3 },
  { key: 'team', name: 'Team', priceCents: 3999, seatCap: 10 },
  { key: 'max', name: 'Max', priceCents: 7999, seatCap: 50 },
]

/** Re-point the ladder from `/api/health`. Validated, not trusted — junk
 *  leaves the seed; tiers are re-sorted by cap so the walk below is stable. */
export function hydrateSeatLadder(raw: unknown): void {
  const tiers = (raw as { tiers?: unknown } | null)?.tiers
  if (!Array.isArray(tiers) || tiers.length === 0) return
  const next: SeatLadderTier[] = []
  for (const t of tiers) {
    if (!t || typeof t !== 'object') return
    const tier = t as Partial<Record<keyof SeatLadderTier, unknown>>
    const key = tier.key === 'aska_plus' || tier.key === 'team' || tier.key === 'max' ? tier.key : null
    const priceCents = typeof tier.priceCents === 'number' && Number.isFinite(tier.priceCents) && tier.priceCents >= 0 ? Math.trunc(tier.priceCents) : null
    const seatCap = typeof tier.seatCap === 'number' && Number.isFinite(tier.seatCap) && tier.seatCap > 0 ? Math.trunc(tier.seatCap) : null
    if (!key || priceCents === null || seatCap === null || typeof tier.name !== 'string' || !tier.name) return
    next.push({ key, name: tier.name, priceCents, seatCap })
  }
  next.sort((a, b) => a.seatCap - b.seatCap)
  if (JSON.stringify(next) === JSON.stringify(SEAT_LADDER)) return
  SEAT_LADDER = next
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('aska:seat-plan-changed'))
  }
}

/** The ladder tier an org of `totalSeats` Aska+ seats lands on — the smallest
 *  rung whose cap covers them; null when they fit in the free browse seats
 *  isn't a question this answers (0 ⇒ the first rung; beyond the top cap ⇒
 *  the top rung — a >50-seat org is a talk-to-us, not a bigger number). */
export function ladderTierFor(totalSeats: number): SeatLadderTier {
  const n = Number.isFinite(totalSeats) ? Math.max(1, Math.trunc(totalSeats)) : 1
  for (const t of SEAT_LADDER) {
    if (n <= t.seatCap) return t
  }
  return SEAT_LADDER[SEAT_LADDER.length - 1]
}

/** "Aska+ $18.99/mo (up to 3) · Team $39.99/mo (up to 10) · Max $79.99/mo
 *  (up to 50)" — the ladder in one line. */
export function seatLadderSummary(): string {
  return SEAT_LADDER
    .map((t) => `${t.name} ${formatSeatPrice(t.priceCents)}/mo (up to ${t.seatCap})`)
    .join(' · ')
}
